import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { setPageJsonLd, removePageJsonLd } from '../../utils/jsonLd';

export default function ReviewsPage() {
  useEffect(() => {
    document.title = 'Expert Gear Reviews | Shocker Solutions';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'In-depth reviews of solar generators, off-grid refrigeration, and camping showers. Real-world testing of Jackery, SetPower, and outdoor equipment for off-grid living.');
    }
    
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.shockersolutions.com/reviews');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.shockersolutions.com';

    setPageJsonLd({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${siteUrl}/reviews`,
      url: `${siteUrl}/reviews`,
      name: 'Expert Gear Reviews | Shocker Solutions',
      description: 'In-depth reviews of solar generators, off-grid refrigeration, and camping showers. Real-world testing of Jackery, SetPower, and outdoor equipment.',
      publisher: {
        '@type': 'Organization',
        name: 'Shocker Solutions',
        url: siteUrl,
      },
      hasPart: [
        { '@type': 'Article', url: `${siteUrl}/reviews/jackery-homepower-3000`, name: 'Jackery Solar Generator HomePower 3000 Review' },
        { '@type': 'Article', url: `${siteUrl}/reviews/jackery-explorer-1000-plus`, name: 'Jackery Explorer 1000 Plus Review' },
        { '@type': 'Article', url: `${siteUrl}/reviews/setpower-aj40`, name: 'SetPower AJ40 Portable Refrigerator Review' },
        { '@type': 'Article', url: `${siteUrl}/reviews/setpower-rv-55l`, name: 'SetPower RV Refrigerator 55L Review' },
        { '@type': 'Article', url: `${siteUrl}/reviews/camplux-nano-3`, name: 'Camplux Nano 3 Battery Powered Water Heater Review' },
        { '@type': 'Article', url: `${siteUrl}/reviews/solar-camping-shower-bag`, name: 'Solar Camping Shower Bag Review' },
      ],
    });

    return () => removePageJsonLd();
  }, []);

  const reviews = [
    {
      id: 1,
      category: 'Solar Generators',
      title: 'Jackery Solar Generator HomePower 3000 Review (Real-World Weekend Use)',
      excerpt: 'Review is based on consistent weekend use in a very remote area, far from grid power.',
      rating: 5,
      date: 'December 28, 2024',
      image: 'https://static.readdy.ai/image/4cab5009fae4c10096ff71d938618757/9aee64be260098f83487d38b9678fd93.png',
      link: '/reviews/jackery-homepower-3000',
    },
    {
      id: 2,
      category: 'Solar Generators',
      title: 'Jackery Explorer 1000 Plus Review: Best Mid-Range Option',
      excerpt: 'Perfect balance of power and portability for weekend warriors and van lifers.',
      rating: 5,
      date: 'December 25, 2024',
      image: 'https://public.readdy.ai/ai/img_res/929db11aa4240ef5d8cf87979a8c416d.jpg',
      link: '/reviews/jackery-explorer-1000-plus',
    },
    {
      id: 3,
      category: 'Refrigeration',
      title: 'SetPower AJ40 Portable Refrigerator: Keep Food Fresh Anywhere',
      excerpt: 'This 12V portable fridge/freezer combo is a game-changer for extended off-grid trips.',
      rating: 5,
      date: 'December 22, 2024',
      image: 'https://readdy.ai/api/search-image?query=SetPower%20portable%20electric%20cooler%20refrigerator%20in%20camper%20van%20with%20food%20and%20drinks%20inside%20simple%20interior%20background%20modern%20product%20photography%20orange%20accent%20lighting&width=600&height=400&seq=review-setpower-aj40-001&orientation=landscape',
      link: '/reviews/setpower-aj40',
    },
    {
      id: 4,
      category: 'Refrigeration',
      title: 'SetPower RV Refrigerator 55L: Maximum Capacity for Long Trips',
      excerpt: 'Large capacity meets efficient cooling in this premium portable refrigerator.',
      rating: 4,
      date: 'December 20, 2024',
      image: 'https://readdy.ai/api/search-image?query=large%20SetPower%20portable%20refrigerator%20in%20RV%20interior%20with%20organized%20food%20storage%20simple%20clean%20background%20professional%20product%20photography%20warm%20lighting&width=600&height=400&seq=review-setpower-55l-001&orientation=landscape',
      link: '/reviews/setpower-rv-55l',
    },
    {
      id: 5,
      category: 'Hot Showers',
      title: 'Camplux Nano 3 Review: Battery-Powered Portable Water Heater',
      excerpt: 'Rechargeable battery-powered hot water on demand. No propane, no electricity hookup needed.',
      rating: 5,
      date: 'December 18, 2024',
      image: 'https://static.readdy.ai/image/4cab5009fae4c10096ff71d938618757/1a103fe6c8a6684de05441dbffc6c812.png',
      link: '/reviews/camplux-nano-3',
    },
    {
      id: 6,
      category: 'Hot Showers',
      title: 'Solar Camping Shower Bag: Eco-Friendly Hot Water Solution',
      excerpt: 'Harness the sun\'s power for warm showers without any fuel or electricity needed.',
      rating: 4,
      date: 'December 15, 2024',
      image: 'https://readdy.ai/api/search-image?query=solar%20camping%20shower%20bag%20hanging%20from%20tree%20branch%20at%20campsite%20with%20simple%20forest%20background%20eco%20friendly%20outdoor%20gear%20natural%20lighting%20product%20photography&width=600&height=400&seq=review-solar-shower-001&orientation=landscape',
      link: '/reviews/solar-camping-shower-bag',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-4">Gear Reviews</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Honest, detailed reviews based on real-world testing. We put every product through its paces so you can make informed decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {reviews.map((review) => (
              <article key={review.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                <Link to={review.link} className="block relative h-64 w-full overflow-hidden cursor-pointer">
                  <img
                    src={review.image}
                    alt={review.title}
                    title={review.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block bg-orange-500 text-black text-xs font-bold px-4 py-2 rounded-full">
                      {review.category}
                    </span>
                  </div>
                </Link>

                <div className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-base ${i < review.rating ? 'text-orange-500' : 'text-gray-300'}`}
                      ></i>
                    ))}
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 leading-tight min-h-[3.5rem]">
                    <Link to={review.link} className="hover:text-orange-500 transition-colors">
                      {review.title}
                    </Link>
                  </h2>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{review.excerpt}</p>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{review.date}</span>
                    <Link
                      to={review.link}
                      className="inline-flex items-center gap-1 text-orange-500 hover:text-orange-600 font-semibold text-sm transition-colors cursor-pointer"
                    >
                      Read More
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
